/**
 * publicPrometPageModel — the pure half of the public page »Promet po conah«
 * (#867, /javno/promet/<app public_id>): which app the address names, where
 * the map opens, what a failure says, and the basemap's credit. No DOM, no
 * fetch, no map.
 */

import type { FeatureCollection, Geometry, Position } from 'geojson';
import { isAppPublicId } from '../../shared/utils/publicIdRefs.js';
import type { PublicPeriodLimits, PublicPrometGeometry } from '../../shared/utils/ngPrometPublic.js';

export const PAGE_PATH_PREFIX = '/javno/promet/';

/**
 * The app's public id from `/javno/promet/<id>` (one trailing slash allowed),
 * or null. A shape check, never a prefix test (publicIdRefs.ts): a slug or an
 * extra segment names no page.
 */
export function appIdFromPath(pathname: string): string | null {
  if (!pathname.startsWith(PAGE_PATH_PREFIX)) return null;
  const rest = pathname.slice(PAGE_PATH_PREFIX.length).replace(/\/$/, '');
  return isAppPublicId(rest) ? rest : null;
}

/** The canonical address of the page of `appId`. */
export function pagePath(appId: string): string {
  return `${PAGE_PATH_PREFIX}${appId}`;
}

export type Bounds = [west: number, south: number, east: number, north: number];

/** The box around the zones, the line and the access points; null when there is nothing to show. */
export function geometryBounds(g: Pick<PublicPrometGeometry, 'zones' | 'lines' | 'points'>): Bounds | null {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  const visit = (p: Position): void => {
    const [x, y] = p;
    if (typeof x !== 'number' || typeof y !== 'number') return;
    west = Math.min(west, x);
    east = Math.max(east, x);
    south = Math.min(south, y);
    north = Math.max(north, y);
  };
  const walk = (geom: Geometry | null): void => {
    if (!geom) return;
    switch (geom.type) {
      case 'Point':
        visit(geom.coordinates);
        break;
      case 'MultiPoint':
      case 'LineString':
        geom.coordinates.forEach(visit);
        break;
      case 'MultiLineString':
      case 'Polygon':
        geom.coordinates.forEach((ring) => ring.forEach(visit));
        break;
      case 'MultiPolygon':
        geom.coordinates.forEach((poly) => poly.forEach((ring) => ring.forEach(visit)));
        break;
      case 'GeometryCollection':
        geom.geometries.forEach(walk);
        break;
    }
  };
  const each = (fc: FeatureCollection): void => fc.features.forEach((f) => walk(f.geometry));
  each(g.zones);
  each(g.lines);
  each(g.points);
  return Number.isFinite(west) ? [west, south, east, north] : null;
}

export const PAGE_COPY = {
  unavailable: 'Ta stran ni na voljo.',
  failed: 'Podatkov ni bilo mogoče naložiti. Poskusite znova.',
  /**
   * The notice on top (MONG: say on the page what is shown and the limits):
   * the categories are fixed (#867), the weekdays free (#975), the three caps.
   */
  notice: (l: PublicPeriodLimits): string =>
    'Javni prikaz. Prikazan je vedno seštevek osebnih vozil, tovornih vozil in nočnega prometa (kategorij prometa ni ' +
    'mogoče izbirati); izberete lahko dneve v tednu. ' +
    `Referenčno obdobje (R) je lahko dolgo največ ${l.referenceDays} dni, obdobje za primerjavo (P) največ ` +
    `${l.comparisonDays} dni, od začetka prvega do konca zadnjega obdobja pa je lahko največ ${l.windowDays} dni.`,
};

/** What the page says when its first request fails. A 404 = no such public page. */
export function pageFailureText(status: number): string {
  return status === 404 ? PAGE_COPY.unavailable : PAGE_COPY.failed;
}

interface StyleLike {
  sources?: Record<string, { attribution?: unknown }>;
  layers?: Array<{ source?: unknown }>;
}

/**
 * The basemap's credits. The CDN styles keep the MapTiler / OpenStreetMap
 * credit on a source that no layer draws (the drawn source carries none), and
 * MapLibre's control lists only the sources in use — so the page names the
 * credits of the sources no layer references itself.
 */
export function basemapCredits(style: StyleLike | null | undefined): string[] {
  if (!style?.sources) return [];
  const used = new Set((style.layers ?? []).map((l) => l.source).filter((s): s is string => typeof s === 'string'));
  const out: string[] = [];
  for (const [id, source] of Object.entries(style.sources)) {
    const text = source?.attribution;
    if (!used.has(id) && typeof text === 'string' && text.trim() && !out.includes(text)) out.push(text);
  }
  return out;
}
